import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Form } from "react-bootstrap";
import Select from "react-select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import DatePicker from "../Common/Component/DatePicker";
import TimePicker from "../Common/Component/TimePicker";
import { joinStoredDateTime, splitStoredDateTime } from "../../utils/dateTime/dateFormat";
import { masterEquipment } from "../ManageEquipment/service/equipment-service";
import {
    SAMPLE_COLLECTION_STATUSES,
    addSampleCollection,
    recollectSample as recollectSampleRequest,
} from "../CollectedSample/service/sample-collection-service";

const THEME = "#1b255b";

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: "38px",
        borderColor: state.isFocused ? THEME : "#ced4da",
        boxShadow: state.isFocused ? `0 0 0 2px rgba(27, 37, 91, 0.12)` : "none",
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: THEME,
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: "#fff",
        fontWeight: 500,
        fontSize: "12px",
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: "#fecaca",
        ":hover": {
            backgroundColor: "#b91c1c",
            color: "#fff",
        },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
};

const toEquipmentOptions = (list = []) =>
    (Array.isArray(list) ? list : [])
        .map((item) => {
            if (!item) return null;
            if (typeof item === "string") {
                return { value: item, label: item };
            }
            const eqId = item._id || item.value;
            if (!eqId) return null;
            return {
                value: eqId,
                label: item.code ? `${item.name} (${item.code})` : item.name || eqId,
            };
        })
        .filter(Boolean);

const nowLocalDateTime = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatCollectionTime = (value) => {
    if (!value) return new Date().toISOString();

    // datetime-local: "YYYY-MM-DDTHH:mm" → ISO UTC "YYYY-MM-DDTHH:mm:00.000Z"
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
        return new Date(`${value}:00.000Z`).toISOString();
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return new Date().toISOString();
    return d.toISOString();
};

const getOrderBarcodeId = (order) =>
    order?.orderBarcodeId || order?.barcodeId || order?.orderBarcode || "";

const equipmentIdsFrom = (list = []) =>
    (list || [])
        .map((eq) => (typeof eq === "string" ? eq : eq?._id))
        .filter(Boolean);

const sampleTypeIdOf = (test) => {
    const value = test?.sampleTypeId;
    if (value && typeof value === "object") {
        return String(value._id || value.sampleId || "").trim();
    }
    return String(value || "").trim();
};

const getSamplesFromTest = (test) => {
    if (!test) return [];
    const fromList = Array.isArray(test.samples) ? test.samples : [];
    const typeId = sampleTypeIdOf(test);
    if (!typeId) return fromList;
    if (fromList.some((sample) => String(sample.sampleId || sample._id || "") === typeId)) {
        return fromList;
    }
    return [
        ...fromList,
        {
            sampleId: typeId,
            name: test.sampleTypeName || "Sample",
            description: "",
            assignedEquipment: test.assignedEquipment || [],
        },
    ];
};

const sampleForTest = (test) => {
    const typeId = sampleTypeIdOf(test);
    const samples = getSamplesFromTest(test);
    const match = samples.find((sample) => String(sample.sampleId || sample._id || "") === typeId);
    if (match) return match;
    return samples[0] || null;
};

const uniqueSamples = (tests = []) => {
    const map = new Map();
    tests.forEach((test) => {
        getSamplesFromTest(test).forEach((sample) => {
            const id = sample.sampleId || sample._id;
            if (!id || map.has(id)) return;
            map.set(id, sample);
        });
    });
    return Array.from(map.values());
};

/** Prefer the sample type shared by the most selected tests. */
const pickDefaultSample = (tests = []) => {
    const counts = new Map();
    tests.forEach((test) => {
        const seen = new Set();
        getSamplesFromTest(test).forEach((sample) => {
            const id = sample.sampleId || sample._id;
            if (!id || seen.has(id)) return;
            seen.add(id);
            const current = counts.get(id);
            counts.set(id, { sample, count: (current?.count || 0) + 1 });
        });
    });
    return Array.from(counts.values()).sort((a, b) => b.count - a.count)[0]?.sample || null;
};

const slugifySample = (name) =>
    String(name || "sample")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "sample";

/** Stable per physical-sample form. Shared by every test expanded from that form. */
const createPhysicalSampleKey = (sampleName) =>
    `${slugifySample(sampleName)}-${Math.random().toString(36).slice(2, 7)}`;

const emptyPhysicalSample = (order, tests = []) => {
    const sample = pickDefaultSample(tests) || {};
    const sampleName = sample.name || "";
    return {
        key: `sample-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        physicalSampleKey: createPhysicalSampleKey(sampleName),
        testOrderId: order?._id || "",
        orderBarcodeId: getOrderBarcodeId(order),
        testIds: tests.map((test) => test.testId).filter(Boolean),
        sampleId: sample.sampleId || sample._id || "",
        sampleName,
        sampleDescription: sample.description || "",
        notes: "",
        status: "Collected",
        collectionTime: nowLocalDateTime(),
        assignedEquipmentIds: equipmentIdsFrom(sample.assignedEquipment),
    };
};

/**
 * One form per required sample. Tests that share sampleTypeId stay on the same form.
 * A different sampleTypeId opens its own form.
 */
const buildDefaultRows = (order) => {
    const tests = order?.allTests || [];
    if (!order?._id || tests.length === 0) return [];

    const groups = new Map();
    tests.forEach((test, index) => {
        const matchedSample = sampleForTest(test);
        const sampleTypeId = sampleTypeIdOf(test) || matchedSample?.sampleId || matchedSample?._id;
        const key = sampleTypeId ? String(sampleTypeId) : `test-${test.testId || index}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(test);
    });

    return Array.from(groups.values()).map((groupTests) => {
        const sample = sampleForTest(groupTests[0]) || {};
        const row = emptyPhysicalSample(order, groupTests);
        const sampleName = sample.name || groupTests[0]?.sampleTypeName || row.sampleName || "";
        return {
            ...row,
            sampleId: sample.sampleId || sample._id || row.sampleId,
            sampleName,
            sampleDescription: sample.description || row.sampleDescription || "",
            physicalSampleKey: createPhysicalSampleKey(sampleName),
            assignedEquipmentIds: sample.assignedEquipment
                ? equipmentIdsFrom(sample.assignedEquipment)
                : row.assignedEquipmentIds,
        };
    });
};

const buildRecollectRow = (order, sample = {}) => {
    const tests = order?.allTests || [];
    const matchedTest = tests.find((test) => test.testId === sample.testId);
    const seedTests = matchedTest
        ? [matchedTest]
        : sample.testId
            ? [{
                testId: sample.testId,
                name: sample.testName || "Test",
                sampleTypeId: sample.sampleId,
                sampleTypeName: sample.sampleName,
            }]
            : [];
    const row = emptyPhysicalSample(order, seedTests);
    const equipmentIds = equipmentIdsFrom(sample.assignedEquipment);
    const sampleName = sample.sampleName || row.sampleName || "";

    return {
        ...row,
        status: "Re-Collected",
        notes: "",
        testIds: sample.testId ? [sample.testId] : row.testIds.slice(0, 1),
        sampleId: sample.sampleId || row.sampleId,
        sampleName,
        sampleDescription: sample.sampleDescription || row.sampleDescription || "",
        assignedEquipmentIds: equipmentIds.length ? equipmentIds : row.assignedEquipmentIds,
        physicalSampleKey: createPhysicalSampleKey(sampleName),
    };
};

export default function CollectSamplesModal({
    show,
    onHide,
    order,
    onSuccess,
    startEmpty = false,
    redirectOnSuccess = true,
    recollectSampleId = null,
    recollectSample = null,
}) {
    const navigate = useNavigate();
    const { setLoading } = useLoader();
    const [rows, setRows] = useState([]);
    const [equipmentOptions, setEquipmentOptions] = useState([]);

    const isRecollect = Boolean(recollectSampleId);
    const patientName = order?.patient?.name || recollectSample?.patientName || "—";

    const testOptions = useMemo(() => {
        return (order?.allTests || []).map((test) => ({
            value: test.testId,
            label: test.name,
            test,
        }));
    }, [order]);

    const allTestOption = { label: "Select All", value: "*" };

    const testSelectOptions = useMemo(() => {
        if (isRecollect) return testOptions;
        return testOptions.length > 1 ? [allTestOption, ...testOptions] : testOptions;
    }, [testOptions, isRecollect]);

    const payloadCount = useMemo(
        () => rows.reduce((sum, row) => sum + (row.testIds?.length || 0), 0),
        [rows]
    );

    useEffect(() => {
        if (!show || !order) return;
        if (recollectSampleId) {
            setRows([buildRecollectRow(order, recollectSample || {})]);
        } else {
            setRows(startEmpty ? [emptyPhysicalSample(order, [])] : buildDefaultRows(order));
        }
        fetchEquipment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, order?._id, startEmpty, recollectSampleId]);

    const fetchEquipment = async () => {
        try {
            const res = await masterEquipment();
            if (res.data.success === true) {
                setEquipmentOptions(toEquipmentOptions(res.data.data || []));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const updateRow = (index, patch) => {
        setRows((prev) =>
            prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
        );
    };

    const removeRow = (index) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    const addRow = () => {
        if (!order?.allTests?.length) {
            toast.info("No tests available on this order");
            return;
        }
        setRows((prev) => [...prev, emptyPhysicalSample(order, [])]);
    };

    const testsByIds = (testIds = []) =>
        (order?.allTests || []).filter((test) => testIds.includes(test.testId));

    const handleTestsChange = (index, selected, currentRow) => {
        const picked = selected || [];
        const hasSelectAll = picked.some((opt) => opt.value === "*");
        let nextTests = [];

        if (hasSelectAll) {
            const allSelected = (currentRow.testIds || []).length === testOptions.length;
            nextTests = allSelected ? [] : (order?.allTests || []);
        } else {
            nextTests = picked
                .filter((opt) => opt.value !== "*")
                .map((opt) => opt.test)
                .filter(Boolean);
        }

        const nextIds = nextTests.map((test) => test.testId).filter(Boolean);
        const sampleOptions = uniqueSamples(nextTests);
        const currentSample = sampleOptions.find(
            (sample) => (sample.sampleId || sample._id) === currentRow.sampleId
        );
        const nextSample = currentSample || pickDefaultSample(nextTests) || {};

        const nextSampleName = nextSample.name || "";
        updateRow(index, {
            testIds: nextIds,
            orderBarcodeId: getOrderBarcodeId(order),
            sampleId: nextSample.sampleId || nextSample._id || "",
            sampleName: nextSampleName,
            sampleDescription: nextSample.description || "",
            physicalSampleKey: currentSample
                ? currentRow.physicalSampleKey
                : createPhysicalSampleKey(nextSampleName),
            assignedEquipmentIds: currentSample
                ? currentRow.assignedEquipmentIds
                : equipmentIdsFrom(nextSample.assignedEquipment),
        });
    };

    const getSampleOptionsForTests = (testIds) => {
        return uniqueSamples(testsByIds(testIds)).map((sample) => ({
            value: sample.sampleId || sample._id,
            label: sample.name || "Sample",
            sample,
        }));
    };

    const handleSampleTypeChange = (index, option) => {
        if (!option?.sample) return;
        const sample = option.sample;
        const sampleName = sample.name || "";
        updateRow(index, {
            sampleId: sample.sampleId || sample._id || "",
            sampleName,
            sampleDescription: sample.description || "",
            physicalSampleKey: createPhysicalSampleKey(sampleName),
            assignedEquipmentIds: equipmentIdsFrom(sample.assignedEquipment),
        });
    };

    const handleSubmit = async () => {
        if (!rows.length) {
            toast.info("Add at least one sample to collect");
            return;
        }

        const invalid = rows.find((row) => !row.testIds?.length || !row.sampleId);
        if (invalid) {
            toast.warning("Each sample must have at least one test and a sample type");
            return;
        }

        const toSampleBody = (row, testId) => ({
            testOrderId: row.testOrderId || order._id,
            orderBarcodeId: row.orderBarcodeId || getOrderBarcodeId(order),
            testId,
            sampleId: row.sampleId,
            sampleDescription: row.sampleDescription || "",
            notes: row.notes || "",
            status: row.status || (isRecollect ? "Re-Collected" : "Collected"),
            collectionTime: formatCollectionTime(row.collectionTime),
            assignedEquipment: row.assignedEquipmentIds || [],
            physicalSampleKey: row.physicalSampleKey || createPhysicalSampleKey(row.sampleName),
        });

        setLoading(true);
        try {
            const res = isRecollect
                ? await recollectSampleRequest(recollectSampleId, toSampleBody(rows[0], rows[0].testIds[0]))
                : await addSampleCollection({
                    samples: rows.flatMap((row) =>
                        (row.testIds || []).map((testId) => toSampleBody(row, testId))
                    ),
                });
            if (res.data.success === true) {
                toast.success(
                    res.data.message ||
                        (isRecollect ? "Sample recollected successfully." : "Samples collected successfully.")
                );
                await onSuccess?.(res.data.data);
                onHide();
                if (redirectOnSuccess) {
                    navigate(`${process.env.PUBLIC_URL}/collected-sample`);
                }
            } else {
                toast.warning(
                    res.data.message ||
                        (isRecollect ? "Unable to recollect sample" : "Unable to collect samples")
                );
            }
        } catch (error) {
            console.log(error);
            toast.warning(
                error?.response?.data?.message ||
                    (isRecollect ? "Unable to recollect sample" : "Unable to collect samples")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Collect Samples
                    <div className="text-muted fw-normal" style={{ fontSize: "0.85rem" }}>
                        {patientName}
                        {order?._id ? ` · Order ${String(order._id).slice(-6)}` : ""}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="text-muted small">
                        {isRecollect
                            ? "Recollect one sample. Choose a single test and sample type, then submit."
                            : "One form is one physical sample. Select one or more tests that share it. Add another sample for an extra tube or a different sample type."}
                    </div>
                    {isRecollect ? null : (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={addRow}
                        >
                            <Plus size={14} />
                            Add Sample
                        </Button>
                    )}
                </div>

                {rows.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        No tests found on this order. Click &quot;Add Sample&quot; to create a physical sample.
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {rows.map((row, index) => {
                            const selectedEquipment = (row.assignedEquipmentIds || []).map((id) => {
                                const match = equipmentOptions.find((opt) => opt.value === id);
                                return match || { value: id, label: id };
                            });
                            const sampleOptions = getSampleOptionsForTests(row.testIds);
                            const selectedTests = (row.testIds || []).map((id) => {
                                const match = testOptions.find((opt) => opt.value === id);
                                return match || { value: id, label: id };
                            });
                            const collectionParts = splitStoredDateTime(row.collectionTime);

                            return (
                                <div
                                    key={row.key}
                                    className="border rounded p-3"
                                    style={{ background: "#f8f9fb" }}
                                >
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <strong style={{ color: THEME }}>
                                            Physical sample #{index + 1}
                                            <span className="fw-normal text-muted ms-2" style={{ fontSize: "0.8rem" }}>
                                                {(row.testIds || []).length} test{(row.testIds || []).length === 1 ? "" : "s"}
                                            </span>
                                        </strong>
                                        {isRecollect ? null : (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                title="Remove"
                                                onClick={() => removeRow(index)}
                                                disabled={rows.length === 1}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <Form.Label className="small mb-1">Tests</Form.Label>
                                            <Select
                                                isMulti={!isRecollect}
                                                closeMenuOnSelect={isRecollect}
                                                hideSelectedOptions={false}
                                                options={testSelectOptions}
                                                value={isRecollect ? selectedTests[0] || null : selectedTests}
                                                onChange={(selected) =>
                                                    handleTestsChange(
                                                        index,
                                                        isRecollect ? (selected ? [selected] : []) : selected,
                                                        row
                                                    )
                                                }
                                                placeholder={isRecollect ? "Select a test..." : "Select one or more tests..."}
                                                styles={selectStyles}
                                                classNamePrefix="react-select"
                                                menuPortalTarget={document.body}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <Form.Label className="small mb-1">Sample Type</Form.Label>
                                            <Select
                                                options={sampleOptions}
                                                value={
                                                    sampleOptions.find((opt) => opt.value === row.sampleId) ||
                                                    (row.sampleId
                                                        ? { value: row.sampleId, label: row.sampleName || "Sample" }
                                                        : null)
                                                }
                                                onChange={(option) => handleSampleTypeChange(index, option)}
                                                placeholder={row.testIds?.length ? "Select sample type" : "Select tests first"}
                                                isDisabled={!row.testIds?.length}
                                                styles={selectStyles}
                                                classNamePrefix="react-select"
                                                menuPortalTarget={document.body}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <Form.Label className="small mb-1">Status</Form.Label>
                                            <Form.Select
                                                value={row.status}
                                                onChange={(e) =>
                                                    updateRow(index, { status: e.target.value })
                                                }
                                            >
                                                {SAMPLE_COLLECTION_STATUSES.filter((status) =>
                                                    isRecollect
                                                        ? status === "Pending" ||
                                                          status === "Collected" ||
                                                          status === "Re-Collected"
                                                        : status === "Pending" || status === "Collected"
                                                ).map((status) => (
                                                    <option key={status} value={status}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </div>

                                        <div className="col-md-6">
                                            <Form.Label className="small mb-1">Collection Date & Time</Form.Label>
                                            <div className="d-flex gap-2">
                                                <div style={{ flex: "1 1 160px", minWidth: 150 }}>
                                                    <DatePicker
                                                        name={`collectionDate-${row.key}`}
                                                        value={collectionParts.date}
                                                        onChange={(event) => {
                                                            updateRow(index, {
                                                                collectionTime: joinStoredDateTime(
                                                                    event.target.value,
                                                                    collectionParts.time
                                                                ),
                                                            });
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ flex: "1 1 120px", minWidth: 112 }}>
                                                    <TimePicker
                                                        name={`collectionTime-${row.key}`}
                                                        value={collectionParts.time}
                                                        className="form-control"
                                                        onChange={(event) => {
                                                            updateRow(index, {
                                                                collectionTime: joinStoredDateTime(
                                                                    collectionParts.date,
                                                                    event.target.value
                                                                ),
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Label className="small mb-1">Assigned Equipment</Form.Label>
                                            <Select
                                                isMulti
                                                isSearchable
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                options={equipmentOptions}
                                                value={selectedEquipment}
                                                onChange={(selected) =>
                                                    updateRow(index, {
                                                        assignedEquipmentIds: (selected || []).map(
                                                            (item) => item.value
                                                        ),
                                                    })
                                                }
                                                placeholder="Select equipment..."
                                                styles={selectStyles}
                                                classNamePrefix="react-select"
                                                menuPortalTarget={document.body}
                                            />
                                        </div>

                                        <div className="col-12">
                                            <Form.Label className="small mb-1">Notes</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={row.notes || ""}
                                                onChange={(e) =>
                                                    updateRow(index, { notes: e.target.value })
                                                }
                                                placeholder="Collection notes"
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!payloadCount}>
                    {isRecollect ? "Recollect Sample" : `Submit Samples (${payloadCount})`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
